import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import { CheckboxCDS } from '@sharedCR/coreDesignSystem/form';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { deleteByIndex, setIn } from 'utils/data';

import {
  selectAutomatedActionCriteriaSchema,
  selectLoanActionCriteriaSchema,
} from 'store/system/selectors';

import ExpressionField from '../../../form/ExpressionField';
import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';

const LoanActionSavePendingChangesPermissionRow = ({
  index,
  rootPath,
  isDisabled,
  isEditAllowed,
  isSingleItem,
  isAutomatedActions = false,
}) => {
  const {
    form,
    setInForm,
    setValidationErrors,
    validationErrors,
    handleChange,
    validateField,
    staticValidateField,
  } = useFormContext();
  const automatedSchemaParams = useSelector(selectAutomatedActionCriteriaSchema);
  const loanActionSchemaParams = useSelector(selectLoanActionCriteriaSchema);
  const isPendingChangesByUserEnabled = useFeatureToggle(FeatureFlag.PendingChangesByUser);

  const schemaParams = isAutomatedActions ? automatedSchemaParams : loanActionSchemaParams;

  const fieldPath = `${rootPath}[${index}]`;
  const expressionPath = `${fieldPath}.permissionFriendlyExpression`;
  const canCancelPath = `${fieldPath}.canCancel`;
  const canSavePath = `${fieldPath}.canSave`;
  const canViewOthersPath = `${fieldPath}.canViewOthers`;

  const permissions = _.get(form, rootPath);
  const canCancel = _.get(form, canCancelPath);
  const canSave = _.get(form, canSavePath);
  const canViewOthers = _.get(form, canViewOthersPath);

  const onDeleteRow = useCallback(() => {
    const newPermissions = [...permissions];

    newPermissions.splice(index, 1);
    setInForm(rootPath, newPermissions);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, permissions, rootPath]);

  const onCanCancelChange = useCallback(
    e => setInForm(canCancelPath, e.target.checked),
    [canCancelPath, setInForm],
  );

  const onCanSaveChange = useCallback(
    e => setInForm(canSavePath, e.target.checked),
    [canSavePath, setInForm],
  );

  const onCanViewOthersChange = useCallback(
    e => setInForm(canViewOthersPath, e.target.checked),
    [canViewOthersPath, setInForm],
  );

  return (
    <Box className="flex flex-col gap-8 mt-8 mb-8">
      <Box className="flex">
        <ExpressionField
          isOnlyBooleanAllowed
          required
          className="grow"
          dataTestId={loanManagementTestIds.getSavePendingChangesExpressionInputId(fieldPath)}
          disabled={isDisabled || !isEditAllowed}
          error={!!_.get(validationErrors, expressionPath)}
          handleChange={handleChange}
          helperText={_.get(validationErrors, expressionPath)}
          id={`savePendingChangesPermission-${index}`}
          label="Permission Expression"
          name={expressionPath}
          schemaParams={schemaParams}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          value={_.get(form, expressionPath) || ''}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </Box>
      <Box className="flex gap-16">
        <CheckboxCDS
          dataTestId={loanManagementTestIds.getSavePendingChangesCanCancelCheckboxId(fieldPath)}
          disabled={isDisabled || !isEditAllowed}
          label="Cancel"
          name={canCancelPath}
          value={!!canCancel}
          onChange={onCanCancelChange}
        />
        <CheckboxCDS
          dataTestId={loanManagementTestIds.getSavePendingChangesCanSaveCheckboxId(fieldPath)}
          disabled={isDisabled || !isEditAllowed}
          label="Save"
          name={canSavePath}
          value={!!canSave}
          onChange={onCanSaveChange}
        />
        {isPendingChangesByUserEnabled && (
          <CheckboxCDS
            dataTestId={loanManagementTestIds.getSavePendingChangesCanViewOthersCheckboxId(
              fieldPath,
            )}
            disabled={isDisabled || !isEditAllowed}
            label="View other users' changes"
            name={canViewOthersPath}
            value={!!canViewOthers}
            onChange={onCanViewOthersChange}
          />
        )}
      </Box>
    </Box>
  );
};

LoanActionSavePendingChangesPermissionRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionSavePendingChangesPermissionRow);
